/* eslint camelcase: 0 */
import { localeMapping, htmlTriplaClassName } from 'javascript/Constants'
import { markdownToHtml } from 'Utilities/markdown'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { useAppStore } from 'Store/app'

const isMarkdownFAQEnabled = process.env.ENABLE_FAQ_MARKDOWN === 'true'

export function checkIsFullScreen() {
  return /iPhone|iPod|iPad|Android/.test(navigator.userAgent)
}

export function isMobile() {
  let checkUserAgent = false
  const checkScreenWidth = window.innerWidth <= 992
  /* eslint-disable no-useless-escape */
  ;(function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      checkUserAgent = true
  })(navigator.userAgent || navigator.vendor || window.opera)
  /* eslint-enable no-useless-escape */
  return checkUserAgent && checkScreenWidth
}

export function isWindows() {
  return navigator.platform.indexOf('Win') > -1
}

export function checkLinks(url) {
  // this method is for backwards compatibility. only for supporting the old bot code
  const braceText = url.match(/{.+}/)
  if (braceText) {
    const newURL = url.replace(braceText[0], '')
    const display = braceText[0].substring(1, braceText[0].length - 1) // remove the {}
    const result = `<a href="${newURL}" target="_blank">${display}</a>`
    return result
  } else {
    return `<a href="${url}" target="_blank">${url}</a>`
  }
}

export function parseTriplaAppLink(text) {
  return text.replace(/tripla-app:\/\/reservation_page\?reservation_id=.+}/, checkLinks) // this will capture mobile app urls
}

export function parseLegacyLink(text) {
  return text.replace(/https?:\/\/[^\s^<]+/g, checkLinks) // this will capture both https://xy.com AND https://xy.com{Text here}
}

export function formatMarkdownMessage(message) {
  if (!isMarkdownFAQEnabled) return formatChatMessage(message)

  const sanitizedMessage = markdownToHtml(message)
  return parseTriplaAppLink(sanitizedMessage)
}

export function formatChatMessage(chatMessage) {
  if (!chatMessage) return chatMessage
  const sanitizedMessage = chatMessage.replace(/^\s+|\s+$/g, '')
  let convertedLinks = parseLegacyLink(sanitizedMessage)
  convertedLinks = parseTriplaAppLink(convertedLinks)
  return lineBreaks(convertedLinks)
}

export function formatTime(time, format = 24) {
  const d = new Date(time)

  switch (format) {
    case 12:
      const formatted12Time = d.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      })
      return formatted12Time

    case 24:
      const hour = d.getHours() < 10 ? `0${d.getHours()}` : d.getHours()
      const minute = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes()

      return `${hour}:${minute}`

    default:
      break
  }
}

export function formatDate(date, format = 'yyyy-MM-dd') {
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  const str = format
    .replace(/dd/, ('0' + day).slice(-2))
    .replace(/d/, day)
    .replace(/yyyy/, year)
    .replace(/yy/, String(year).slice(2))
    .replace(/MM/, ('0' + month).slice(-2))
    .replace(/M(?!a|ä|e)/, month)
  return str
}

export function findDayAfterToday(day) {
  const now = new Date()
  return new Date(new Date().setDate(now.getDate() + day))
}

export function lineBreaks(message) {
  return message.replace(/(?:\r\n|\r|\n)/g, '<br>')
}

export function navigatorLanguage() {
  const language =
    navigator.language ||
    navigator.browserLanguage ||
    navigator.userLanguage ||
    navigator.systemLanguage
  return language
}

export function setDefaultBrowserLanguage(browserLanguage) {
  const guessedBrowserLanguage = browserLanguage ? guessBrowserLanguage(browserLanguage) : null
  const mappedBrowserLanguage = localeMapping?.hasOwnProperty(guessedBrowserLanguage)
    ? localeMapping[guessedBrowserLanguage]
    : 'en'
  return mappedBrowserLanguage
}

export function guessBrowserLanguage(lang) {
  const lowerCaseLang = lang.toLowerCase()
  const navLangPatterns = {
    en: /^(en(-.*)?)$/,
    ko: /^(ko(-.*)?)$/,
    ja: /^(ja(-.*)?)$/,
    'zh-hans': /^(zh([-_](hans|cn|sg).*)?)$/i,
    'zh-hant': /^(zh([-_](hant|tw|mo|hk).*)?)$/i,
    id: /^(id|in)(-.*)?$/,
    ar: /^(ar(-.*)?)$/,
    th: /^(th(-.*)?)$/
  }

  for (const key in navLangPatterns) {
    if (navLangPatterns[key].test(lowerCaseLang)) {
      return key
    }
  }

  return lang
}

export function mapBrowserLocale(locale) {
  if (/^(zh([-_](hans|cn|sg).*)?)$/i.test(locale)) return 'zh-Hans'
  if (/^(zh([-_](hant|tw|mo|hk).*)?)$/i.test(locale)) return 'zh-Hant'

  return locale
}

export function createAndAppendStyle() {
  const styleString = `
    .${htmlTriplaClassName} {
      overflow: visible!important;
    }
    .${htmlTriplaClassName} > body {
      overflow: hidden;
      height: 100%;
      width: 100%;
      position: fixed;
    }
  `
  const styleTextNode = document.createTextNode(styleString)
  const headElement = document.getElementsByTagName('head')[0]
  const styleTag = document.createElement('style')
  styleTag.id = 'triplabot-styling'
  styleTag.appendChild(styleTextNode)
  headElement.appendChild(styleTag)
}

export function checkImage(imageSrc, fallBackImage, warningMessage = '') {
  const appStore = useAppStore()

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      appStore.setImageReady(true)
      resolve(imageSrc)
    }
    img.onerror = () => {
      if (warningMessage) console.warn(warningMessage)
      if (fallBackImage === false) reject(warningMessage)
      appStore.setImageReady(true)
      resolve(fallBackImage)
    }
    img.src = imageSrc
  })
}

export function touchHeight(height) {
  return (height * window.innerHeight) / window.screen.height
}

export function touchWidth(width) {
  return (width * window.innerWidth) / window.screen.width
}

export function AmPmToTimeString(hours, minutes, ampm) {
  if (ampm === 'PM' && hours < 12) hours = hours + 12
  if (ampm === 'AM' && hours === 12) hours = hours - 12
  if (hours > 23) hours = 0
  if (minutes > 59) minutes = 0
  let sHours = hours.toString()
  let sMinutes = minutes.toString()
  if (hours < 10) sHours = '0' + sHours
  if (minutes < 10) sMinutes = '0' + sMinutes
  return sHours + ':' + sMinutes
}

export function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function matchUrlConditions(urlPatterns, url) {
  if (!urlPatterns || urlPatterns.length === 0) {
    return true
  }

  const lowerCaseURL = url.toLowerCase()
  // We AND the not_contains then OR all the other conditions together
  const notContainsCondition = urlPatterns
    .filter((item) => item.pattern === 'not_contains')
    .every((item) => !lowerCaseURL.includes(item.text.toLowerCase()))

  const otherPatterns = urlPatterns.filter((item) => item.pattern !== 'not_contains')

  const otherConditions =
    otherPatterns.length === 0 ||
    otherPatterns.some((item) => {
      const text = item.text.toLowerCase()
      switch (item.pattern) {
        case 'is':
          return lowerCaseURL === text
        case 'contains':
          return lowerCaseURL.includes(text)
        case 'starts_with':
          return lowerCaseURL.startsWith(text)
        case 'ends_with':
          return lowerCaseURL.endsWith(text)
        default:
          return true
      }
    })

  return notContainsCondition && otherConditions
}

export function hexToRgbA(hex, opacity = '1') {
  let c = null
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')}, ${opacity})`
  }
  throw new Error('Bad Hex')
}

export function pluralize(enText) {
  const lowerEnText = enText.toLowerCase()
  const pluralMap = {
    child: 'ren'
  }

  if (Object.keys(pluralMap).includes(lowerEnText)) {
    return enText + pluralMap[lowerEnText]
  } else {
    return `${enText}s`
  }
}

export function storeBrandHotelOnSwitch(triplabotCode) {
  triplaLocalStorage.setItem('original_brand_hotel_code', triplabotCode)
}

export function clearStoredBrandChat() {
  triplaLocalStorage.removeItem('original_brand_hotel_code')
}

export function storeBranchHotelOnSwitch(triplabotCode) {
  triplaLocalStorage.setItem('branch_chat_switched_to', triplabotCode)
}

export function clearStoredBranchChat() {
  triplaLocalStorage.removeItem('branch_chat_switched_to')
}

export function mapClassNameToArray(className = '', style) {
  const clsArray = className.trim().split(/\s/)
  return clsArray.map((cls) => style[cls])
}

export function upperCamelCase(page) {
  const capitalize = page.charAt(0).toUpperCase() + page.slice(1)
  return camelCase(capitalize)
}

export function camelCase(name) {
  return name.replace(/([-_][a-z])/g, function ($1) {
    return $1.toUpperCase().replace(/[-_]/, '')
  })
}

export function formatPrice(price) {
  return Number(price.match(/\d*/g).join(''))
}

export function formatPriceTitle(price) {
  const priceArray = price.split('¥')
  const formatedPrice = priceArray[1] || priceArray[0]
  return Number(formatedPrice.match(/\d*/g).join(''))
}

export function priceWithComma(price) {
  if (price !== 0 && !Number(price)) return
  return Number(price)
    .toFixed(0)
    .replace(/\d(?=(\d{3})+$)/g, '$&,')
}

export function isJsonString(str) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

export function isSafari() {
  return /^((?!chrome|android|CriOS).)*safari/i.test(navigator.userAgent)
}

export const getDomainWithoutSubdomain = (url) => {
  const urlParts = new URL(url).hostname.split('.')

  return urlParts
    .slice(0)
    .slice(-(urlParts.length === 4 ? 3 : 2))
    .join('.')
}

export const getIsFirstPartyDomainEnabled = (status) => {
  return ['completed', 'update_certificate_pending'].includes(status)
}
